import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import VideoLightbox from "./VideoLightbox";
import { courseVideoAnalytics, WORDPRESS_TAB } from "../services/courseEventService";
import NilEduFeaturedContent from "./NilEduFeaturedContent";
import MediaKitTab from "./MediaKitTab";
import MediaKitShareButton from "./MediaKitShareButton";
import NilDealsTab from "./NilDealsTab";
import CourseTipsModal from "./CourseTipsModal";
import CourseCompletionModal from "./CourseCompletionModal";
import ExpertModal from "./ExpertModal";
import { isAthlete, isGuardian, isAdmin, isStaff } from "../utils/auth";
import { athleteProfileFetchPath } from "../utils/viewedAthlete";
import { buildApiUrl } from "../utils/apiConfig";
import { buildCourseCompletionPayload, completeCourse } from "../services/courseTaskService";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/nil-edu.css";
import "../styles/course-cards.css";
import "../styles/expert.css";
import "../styles/pagination.css";
import "../styles/media-kit.css";
import "../styles/athlete-admin-page-spacing.css";
import { apiClient } from "../utils/apiClient";

type NilEduTab = 'courses' | 'mediaKit' | 'nilDeals';

function resolveNilEduTabFromSearch(search: string): NilEduTab {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'media-kit') {
        return 'mediaKit';
    }
    if (tab === 'nil-deals') {
        return 'nilDeals';
    }
    return 'courses';
}

function nilEduTabQuery(tab: NilEduTab): string {
    if (tab === 'mediaKit') {
        return '?tab=media-kit';
    }
    if (tab === 'nilDeals') {
        return '?tab=nil-deals';
    }
    return '';
}

interface Expert {
    id: number;
    slug: string;
    title: string;
    expertImageUrl: string;
    subtitle: string;
    excerpt: string;
    buttonLabel: string;
    buttonUrl: string;
    modalHeading: string;
    modalContent: string;
    modalCloseLabel: string;
}

interface NilCourse {
    id: number;
    slug: string;
    title: string;
    featuredImageUrl: string;
    subtitle: string;
    buttonLabel: string;
    buttonUrl: string;
    launchModalHeading: string;
    launchModalContent: string;
    launchModalButton: string;
    launchModalUrl: string;
    courseCompletionModalHeading: string;
    courseCompletionModalImageId: number | null;
    courseCompletionModalImageUrl: string;
    courseCompletionModalContent: string;
    courseCompletionModalBadgeId: number | null;
    courseCompletionModalBadgeUrl: string;
    courseCompletionModalDownloadUrl: string;
    courseCompletionModalCloseLabel: string;
    courseCompletionModalLinkText: string;
    courseCompletionModalIconId: number | null;
    courseCompletionModalIconUrl: string;
    expert: Expert | null;
    isCompleted: boolean;
}

interface NilEduApiResponse {
    courses: NilCourse[];
}

interface CoachData {
    id?: string;
    firstName?: string;
    lastName?: string;
}

export default function NilEduPage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [courses, setCourses] = useState<NilCourse[]>([]);
    const [coachData, setCoachData] = useState<CoachData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentCoursePage, setCurrentCoursePage] = useState(1);
    const [showExpertModal, setShowExpertModal] = useState(false);
    const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
    const [selectedCourse, setSelectedCourse] = useState<NilCourse | null>(null);
    const [showCourseTipsModal, setShowCourseTipsModal] = useState(false);
    const [showVideoLightbox, setShowVideoLightbox] = useState(false);
    const [showCourseCompletionModal, setShowCourseCompletionModal] = useState(false);
    const [activeTab, setActiveTab] = useState<NilEduTab>(() => resolveNilEduTabFromSearch(location.search));

    useEffect(() => {
        setActiveTab(resolveNilEduTabFromSearch(location.search));
    }, [location.search]);

    const [mediaKitShareState, setMediaKitShareState] = useState<{ completed: boolean; athleteUserId?: number }>({
        completed: false,
    });

    const COURSES_PER_PAGE = 3;

    // Check if current user is admin/staff viewing an athlete profile
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && Boolean(athleteId);
    const mediaKitReadOnly = isGuardian();

    // NIL deals: athletes manage their own; admins/staff manage on behalf of the athlete.
    // Coaches and guardians are read-only (view deals and agreements only).
    const canMutateNilDeals = isAthlete() || isAdmin() || isStaff();

    // Keep the address bar in sync so tabs are deep-linkable and back/forward works.
    const handleTabChange = (tab: NilEduTab) => {
        setActiveTab(tab);
        navigate({ search: nilEduTabQuery(tab) }, { replace: true });
    };

    // Get team member info from navigation state
    const navState = location.state as AthleteAdminNavState | null;

    const athleteBreadcrumbName = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);

    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId)
    );

    // Handler for breadcrumb navigation to team member view
    const handleNavigateToTeamMember = () => {
        navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const headers: HeadersInit = { Accept: "application/json" };

                // Get team member info from navigation state if available
                if (navState?.teamMemberName) {
                    // Use the team member name from navigation state
                    const nameParts = navState.teamMemberName.split(' ');
                    setCoachData({
                        id: navState.teamMemberId,
                        firstName: nameParts[0] || navState.teamMemberName,
                        lastName: nameParts.slice(1).join(' ') || '',
                    });
                }

                // Determine which athlete username/email to use (self, admin URL, or guardian-linked)
                let targetAthleteUsername = "";
                const profilePath = athleteProfileFetchPath(athleteId);
                if (profilePath) {
                    try {
                        const athleteRes = await apiClient(buildApiUrl(profilePath), {
                            headers,
                            credentials: "include",
                        });
                        if (athleteRes.ok) {
                            const athleteData = await athleteRes.json();
                            targetAthleteUsername = athleteData.username || athleteData.email || "";
                        }
                    } catch (meError) {
                        console.error("Failed to fetch athlete username:", meError);
                    }
                }

                // Fetch NIL EDU courses from API
                try {
                    // Include username parameter to get completion status for athlete
                    const coursesUrl = targetAthleteUsername
                        ? `/api/nil-edu/courses?username=${encodeURIComponent(targetAthleteUsername)}`
                        : `/api/nil-edu/courses`;

                    const coursesRes = await apiClient(
                        buildApiUrl(coursesUrl),
                        {
                            headers,
                            credentials: "include"
                        }
                    );

                    if (coursesRes.ok) {
                        const data: NilEduApiResponse = await coursesRes.json();
                        // Set courses from the response (expert is now nested in each course)
                        setCourses(Array.isArray(data.courses) ? data.courses : []);
                    } else {
                        console.warn("Could not fetch NIL EDU courses:", coursesRes.status);
                        setCourses([]);
                    }
                } catch (coursesError) {
                    console.error("Error fetching NIL EDU courses:", coursesError);
                    setCourses([]);
                }
            } catch (error) {
                console.error("Failed to fetch NIL EDU data:", error);
                setError("Failed to load NIL EDU content");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [athleteId, navState?.teamMemberId, navState?.teamMemberName]);

    // Pagination logic for courses
    const totalCoursePages = Math.ceil(courses.length / COURSES_PER_PAGE);
    const currentCourses = courses.slice(
        (currentCoursePage - 1) * COURSES_PER_PAGE,
        currentCoursePage * COURSES_PER_PAGE
    );


    // Handler for opening expert modal
    const handleLearnMore = (expert: Expert) => {
        setSelectedExpert(expert);
        setShowExpertModal(true);
    };

    // Handler for closing expert modal
    const handleCloseModal = () => {
        setShowExpertModal(false);
        setSelectedExpert(null);
    };

    // Handler for clicking Launch button on course card
    const handleLaunchCourse = (course: NilCourse) => {
        setSelectedCourse(course);
        setShowCourseTipsModal(true);
    };

    // Handler for clicking Launch button on tips modal
    const handleStartVideo = () => {
        setShowCourseTipsModal(false);
        setShowVideoLightbox(true);
    };

    // Handler for when video playback ends
    const handleVideoEnd = () => {
        if (!selectedCourse) {
            return;
        }

        const completedCourseId = selectedCourse.id;
        setCourses((prev) =>
            prev.map((course) =>
                course.id === completedCourseId ? { ...course, isCompleted: true } : course,
            ),
        );

        void completeCourse(buildCourseCompletionPayload(selectedCourse)).catch((error) => {
            console.error("Failed to persist NIL course completion:", error);
        });

        setShowVideoLightbox(false);
        setShowCourseCompletionModal(true);
    };

    // Handler for closing video lightbox
    const handleCloseVideoLightbox = () => {
        setShowVideoLightbox(false);
        setSelectedCourse(null);
    };

    // Handler for closing course completion modal
    const handleCloseCourseCompletion = () => {
        setShowCourseCompletionModal(false);
        setSelectedCourse(null);
    };

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="NIL"
            teamMemberLabel={getTeamMemberBreadcrumbLabel(
                coachData,
                navState?.teamMemberName,
                athleteStaffName ?? undefined
            )}
            onTeamMemberClick={handleNavigateToTeamMember}
            athleteLabel={athleteBreadcrumbName}
            athleteProfileId={athleteId}
        />
    ) : null;

    if (loading) {
        return (
            <div className={`nil-edu-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className="nil-edu-container">
                    <div className="loading-spinner">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`nil-edu-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
                {adminBreadcrumb}
                <div className="nil-edu-container">
                    <div className="error-message">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`nil-edu-wrapper ${isAdminViewingAthlete ? 'athlete-admin-view' : ''}`}>
            {adminBreadcrumb}

            {/* Main Content Area */}
            <div className={`nil-edu-container ${isAdminViewingAthlete ? 'with-athlete-sidebar' : ''}`}>
                <div className="nil-edu-tabs-row">
                    <div className="nil-edu-tabs" role="tablist">
                        <button
                            role="tab"
                            className={`tab ${activeTab === 'courses' ? 'active' : ''}`}
                            onClick={() => handleTabChange('courses')}
                            aria-selected={activeTab === 'courses'}
                            data-testid="nil-edu-tab-courses"
                        >
                            Courses
                        </button>
                        <button
                            role="tab"
                            className={`tab ${activeTab === 'mediaKit' ? 'active' : ''}`}
                            onClick={() => handleTabChange('mediaKit')}
                            aria-selected={activeTab === 'mediaKit'}
                            data-testid="nil-edu-tab-media-kit"
                        >
                            Media Kit
                        </button>
                        <button
                            role="tab"
                            className={`tab ${activeTab === 'nilDeals' ? 'active' : ''}`}
                            onClick={() => handleTabChange('nilDeals')}
                            aria-selected={activeTab === 'nilDeals'}
                            data-testid="nil-edu-tab-nil-deals"
                        >
                            Opportunities
                        </button>
                    </div>
                    {activeTab === 'mediaKit' && mediaKitShareState.completed && mediaKitShareState.athleteUserId != null && !isAdminViewingAthlete && (
                        <MediaKitShareButton athleteUserId={mediaKitShareState.athleteUserId} />
                    )}
                </div>

                {activeTab === 'courses' && (
                <>
                {/* Courses Section */}
                <section className="courses-section">
                    <div className="courses-header">
                        <h2>Courses</h2>
                    </div>

                    {/* Expert Info and Courses Grid - Side by Side Layout */}
                    <div className="courses-content-layout">
                                {/* Expert Info Card - Left Side */}
                                {/*<div className="expert-info-standalone">*/}
                                {/*    {expert ? (*/}
                                {/*        <>*/}
                                {/*            <div className="expert-avatar">*/}
                                {/*                <img src={expert.expertImageUrl} alt={expert.title} />*/}
                                {/*            </div>*/}
                                {/*            <div className="expert-details">*/}
                                {/*                <div className="expert-text">About the expert</div>*/}
                                {/*                <div className="expert-name">{expert.title}</div>*/}
                                {/*                {expert.excerpt && (*/}
                                {/*                    <div className="expert-excerpt" dangerouslySetInnerHTML={{ __html: expert.excerpt }}></div>*/}
                                {/*                )}*/}
                                {/*                <button*/}
                                {/*                    className="learn-more-btn"*/}
                                {/*                    onClick={() => handleLearnMore(expert)}*/}
                                {/*                >*/}
                                {/*                    Learn More*/}
                                {/*                </button>*/}
                                {/*            </div>*/}
                                {/*        </>*/}
                                {/*    ) : (*/}
                                {/*        <>*/}
                                {/*            <div className="expert-avatar">*/}
                                {/*                <img src="/api/placeholder/64/64" alt="NIL Expert" />*/}
                                {/*            </div>*/}
                                {/*            <div className="expert-details">*/}
                                {/*                <div className="expert-text">About the expert</div>*/}
                                {/*                <div className="expert-name">Coming Soon</div>*/}
                                {/*                <button className="learn-more-btn" disabled>*/}
                                {/*                    Learn More*/}
                                {/*                </button>*/}
                                {/*            </div>*/}
                                {/*        </>*/}
                                {/*    )}*/}
                                {/*</div>*/}

                                {/* Courses Grid - Right Side */}
                                <div className="courses-grid">
                                    {currentCourses.map((course) => (
                                        <div key={course.id} className="course-card">
                                            <div className="course-image">
                                                <img src={course.featuredImageUrl} alt={course.title} />
                                            </div>
                                            <div className="course-content">
                                                {/* Expert info and completion badge row */}
                                                <div className="course-top-row">
                                                    {course.expert && (
                                                        <div className="course-expert-info">
                                                            <div className="course-expert-avatar">
                                                                <img src={course.expert.expertImageUrl} alt={course.expert.title} />
                                                            </div>
                                                            <div className="course-expert-details">
                                                                <button
                                                                    className="course-expert-name-link"
                                                                    onClick={() => handleLearnMore(course.expert!)}
                                                                    data-testid={`course-expert-link-${course.id}`}
                                                                >
                                                                    {course.expert.title}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {course.isCompleted && (
                                                        <div className="course-completion-badge" data-testid={`course-completed-${course.id}`}>
                                                            <span className="completion-checkmark">✓</span>
                                                            <span className="completion-text">Complete</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <h4 className="course-name">{course.title}</h4>
                                                <button
                                                    className="launch-btn"
                                                    onClick={() => handleLaunchCourse(course)}
                                                >
                                                    {course.buttonLabel}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                    {/* Pagination for Courses */}
                    {totalCoursePages > 1 && (
                        <div className="pagination">
                            <div className="pagination-info">
                                <span>
                                    Showing <strong>{(currentCoursePage - 1) * COURSES_PER_PAGE + 1}-{Math.min(currentCoursePage * COURSES_PER_PAGE, courses.length)}</strong> of <strong>{courses.length}</strong> courses
                                </span>
                            </div>

                            <div className="pagination-controls">
                                <div className="page-nav">
                                    <button
                                        className="page-btn"
                                        onClick={() => setCurrentCoursePage(Math.max(currentCoursePage - 1, 1))}
                                        disabled={currentCoursePage <= 1}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </button>
                                    {Array.from({ length: totalCoursePages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            className={`page-number ${page === currentCoursePage ? "page-number-active" : ""}`}
                                            onClick={() => setCurrentCoursePage(page)}
                                            aria-label={`Page ${page}`}
                                            aria-current={page === currentCoursePage ? 'page' : undefined}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        className="page-btn"
                                        onClick={() => setCurrentCoursePage(Math.min(currentCoursePage + 1, totalCoursePages))}
                                        disabled={currentCoursePage >= totalCoursePages}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    </section>
                    {/* Featured Resources Section */}
                    <section className="featured-resources-section">
                        <h2>Resources</h2>
                        <NilEduFeaturedContent limit={6} showPagination={true} />
                    </section>
                </>
                )}

                {activeTab === 'mediaKit' && (
                    <MediaKitTab
                        athleteProfileId={athleteId}
                        readOnly={Boolean(isAdminViewingAthlete) || mediaKitReadOnly}
                        onCompletedKitChange={setMediaKitShareState}
                    />
                )}

                {activeTab === 'nilDeals' && (
                    <NilDealsTab
                        athleteProfileId={athleteId}
                        canMutate={canMutateNilDeals}
                    />
                )}
            </div>

            {/* Expert Modal */}
            {showExpertModal && selectedExpert && (
                <ExpertModal
                    expert={selectedExpert}
                    onClose={handleCloseModal}
                />
            )}

            {/* Course Tips Modal - shown when Launch button is clicked */}
            {showCourseTipsModal && selectedCourse && (
                <CourseTipsModal
                    isOpen={showCourseTipsModal}
                    onClose={() => setShowCourseTipsModal(false)}
                    onLaunchCourse={handleStartVideo}
                    courseTitle={selectedCourse.title}
                    modalHeading={selectedCourse.launchModalHeading}
                    modalContent={selectedCourse.launchModalContent}
                    modalButtonLabel={selectedCourse.launchModalButton}
                />
            )}

            {/* Video Lightbox - shown after tips modal */}
            {showVideoLightbox && selectedCourse && (
                <VideoLightbox
                    open={showVideoLightbox}
                    resource={{
                        id: selectedCourse.id,
                        title: selectedCourse.title,
                        vimeoUrl: selectedCourse.launchModalUrl
                    }}
                    onClose={handleCloseVideoLightbox}
                    onVideoEnd={() => handleVideoEnd()}
                    analytics={courseVideoAnalytics(WORDPRESS_TAB.NIL_EDU, selectedCourse.id)}
                />
            )}

            {/* Course Completion Modal - shown after video ends */}
            {showCourseCompletionModal && selectedCourse && (
                <CourseCompletionModal
                    isOpen={showCourseCompletionModal}
                    onClose={handleCloseCourseCompletion}
                    modalHeading={selectedCourse.courseCompletionModalHeading}
                    modalImageUrl={selectedCourse.courseCompletionModalImageUrl}
                    modalContent={selectedCourse.courseCompletionModalContent}
                    modalBadgeUrl={selectedCourse.courseCompletionModalBadgeUrl}
                    modalDownloadUrl={selectedCourse.courseCompletionModalDownloadUrl}
                    modalCloseLabel={selectedCourse.courseCompletionModalCloseLabel}
                    modalLinkText={selectedCourse.courseCompletionModalLinkText}
                    modalIconUrl={selectedCourse.courseCompletionModalIconUrl}
                />
            )}
        </div>
    );
}
